<template>
	<div class="card">

		<h2 class="mb-3">Reports</h2>
		<div class="formgrid grid align-items-end mb-1">

			<div class="field col-4">
				<Dropdown v-model="selectedReport"
						  :options="reports"
						  optionLabel="label"
						  placeholder="Select a report..."
						  class="w-100pc"
						  @change="onReportChange">
					<template #value="{ value, placeholder }">
						<i class="pi pi-chart-line mr-2" />
						<span>{{ value ? value.label : placeholder }}</span>
					</template>
				</Dropdown>
			</div>

			<!-- Inputs, driven by the selected report's field list -->
			<template v-if="selectedReport">
				<div v-if="selectedReport.fields.includes(ReportField.Search)" :class="['field', firstRowIsSearchFilterOnly ? 'col-8' : 'col-4']">
					<label class="block mb-1">{{ selectedReport.searchLabel || 'Search' }}</label>
					<div class="p-input-icon-left w-100pc">
						<i :class="selectedReport.searchIcon || 'pi pi-home'" />
						<InputText v-model="filters.search" placeholder="Keyword filter..." class="w-100pc" />
					</div>
				</div>

				<div v-if="selectedReport.fields.includes(ReportField.Service)" class="field col-4">
					<label class="block mb-1">Service</label>
					<Dropdown v-model="filters.selectedService"
							  :options="servicesList"
							  optionLabel="serviceName"
							  placeholder="Select service..."
							  :filter="!selectedReport.serviceDisabled"
							  :disabled="selectedReport.serviceDisabled"
							  filterPlaceholder="Find a service"
							  class="w-100pc">
						<template #value="{ value, placeholder }">
							<i class="pi pi-fw pi-bookmark mr-2" />
							<span>{{ value ? value.serviceName : placeholder }}</span>
						</template>
					</Dropdown>
				</div>

				<div v-if="selectedReport.fields.includes(ReportField.City)" class="field col-4">
					<label class="block mb-1">Location</label>
					<div class="p-input-icon-left w-100pc">
						<i class="pi pi-globe" />
						<InputText v-model="filters.city" placeholder="Location..." class="w-100pc" />
					</div>
				</div>

				<template v-if="selectedReport.fields.includes(ReportField.Dates)">
					<div />
					<div class="field col-4">
						<label class="block mb-1">Start Date</label>
						<Calendar v-model="filters.startDate" dateFormat="dd/mm/yy" :showIcon="true" class="w-100pc" placeholder="Start date" />
					</div>
					<div class="field col-4">
						<label class="block mb-1">End Date</label>
						<Calendar v-model="filters.endDate" dateFormat="dd/mm/yy" :showIcon="true" class="w-100pc" placeholder="End date" />
					</div>
				</template>
			</template>
		</div>

		<!-- Toggle filters, driven by the selected report's toggle list -->
		<div v-if="selectedReport" class="flex flex-wrap align-items-center gap-4 mb-3">
			<div v-for="toggle in visibleToggleFilters" :key="toggle.key" class="flex align-items-center gap-2">
				<InputSwitch v-model="filters[toggle.key]" />
				<a @click="filters[toggle.key] = !filters[toggle.key]">{{ toggle.label }}</a>
			</div>
			<div v-if="selectedReport.island" class="flex ml-2 mr-2" style="position:relative;width:40px;height:40px;">
				<a :class="['css-image', filters.filterNorthIsland ? 'north-island-icon' : 'north-island-disabled-icon']"
				   v-tooltip.top="'North Island'"
				   style="position:absolute;margin-top:-2px;height:20px;width:30px;"
				   @click="filters.filterNorthIsland = !filters.filterNorthIsland; filters.filterSouthIsland = false;" />
				<a :class="['css-image', filters.filterSouthIsland ? 'south-island-icon' : 'south-island-disabled-icon']"
				   v-tooltip.top="'South Island'"
				   style="position:absolute;margin-top:18px;height:20px;width:30px;"
				   @click="filters.filterSouthIsland = !filters.filterSouthIsland; filters.filterNorthIsland = false;" />
			</div>
		</div>

		<!-- Export -->
		<div v-if="selectedReport" class="flex justify-content-end">
			<Button label="Export to Excel"
					icon="pi pi-file-excel"
					:loading="isExporting"
					@click="exportReport" />
		</div>
	</div>
</template>

<script lang="ts">
	import { Vue, Options } from 'vue-class-component';
	import { Store } from 'vuex';
	import { State } from 'vue';
	import {
		ServiceData, GetAllAccessibleServicesQuery,
		GetPropertyDashboardQuery, GetPropertyListQuery, GetPropertyServiceAuditQuery,
		GetContactListExcelExportQuery, ContactListSearchQuery,
		GetFilteredCompanySummaryListQuery, CustomerLogosExcelExportRequest, FileResponse
	} from '@/api-client/api-client';
	import { CompassApi } from '@/api-client/CompassApiProxy';
	import { GetBwofServiceID, GetOwnerInspectionServiceId } from '@/utils/CompassConstants';
	import { DownloadFromFileResponse } from '@/utils/DownloadFromFileResponse';
	import Toast from '@/utils/Toast';

	// Which input controls a report shows.
	enum ReportField {
		Search = 'search',
		Service = 'service',
		City = 'city',
		Dates = 'dates',
	}

	// Every filter any report might use — only one report shows at a time, so a single object has no conflicts.
	interface ReportFilters {
		search: string;
		city: string;
		selectedService: ServiceData | null;
		startDate: Date | null;
		endDate: Date | null;
		filterNorthIsland: boolean;
		filterSouthIsland: boolean;
		activeProperty: boolean;
		activeService: boolean;
		includeBwofAnniversary: boolean;
		readyToIssue: boolean;
		includeSubServices: boolean;
		onlyActive: boolean;
		includeCustomers: boolean;
		includeSuppliers: boolean;
		includeOwners: boolean;
		includeSiteContacts: boolean;
		includeAuthorities: boolean;
		includeContactsWithoutEmail: boolean;
		allowDuplicateEmails: boolean;
		includeDeletedCompanies: boolean;
		includeUnassignedLogos: boolean;
	}

	interface ToggleDef {
		key: keyof ReportFilters;
		label: string;
		visible?: (filters: ReportFilters) => boolean;
	}

	interface ReportDef {
		key: string;
		label: string;
		fields: ReportField[];
		searchLabel?: string;
		searchIcon?: string;
		serviceDisabled?: boolean;
		island?: boolean;
		defaultServiceId?: number;
		toggles?: ToggleDef[];
		// store is passed in so these stay plain functions with no component-instance dependency.
		run: (filters: ReportFilters, store: Store<State>) => Promise<FileResponse>;
	}

	function blankFilters(): ReportFilters {
		return {
			search: '',
			city: '',
			selectedService: null,
			startDate: null,
			endDate: null,
			filterNorthIsland: false,
			filterSouthIsland: false,
			activeProperty: true,
			activeService: true,
			includeBwofAnniversary: false,
			readyToIssue: false,
			includeSubServices: false,
			onlyActive: true,
			includeCustomers: true,
			includeSuppliers: true,
			includeOwners: true,
			includeAuthorities: true,
			includeSiteContacts: true,
			includeContactsWithoutEmail: false,
			allowDuplicateEmails: true,
			includeDeletedCompanies: false,
			includeUnassignedLogos: false,
		};
	}

	// The service/search/date filters shared by the two property reports.
	function applyPropertyQueryFilters(query: GetPropertyDashboardQuery | GetPropertyServiceAuditQuery, filters: ReportFilters) {
		query.serviceId = filters.selectedService?.serviceId;
		query.queryString = filters.search || undefined;
		query.startDate = filters.startDate ?? undefined;
		query.endDate = filters.endDate ?? undefined;
		query.activeProperty = filters.activeProperty;
		query.activeService = filters.activeService;
	}

	// One builder per report: map the shared filters onto that report's query and call its export.
	function getFilteredCompanySummaryList(filters: ReportFilters, store: Store<State>): Promise<FileResponse> {
		const query = new GetFilteredCompanySummaryListQuery();
		query.queryString = filters.search || undefined;
		query.onlyActive = filters.onlyActive;
		query.includeCustomers = filters.includeCustomers;
		query.includeSuppliers = filters.includeSuppliers;
		query.includeOwners = filters.includeOwners;
		query.includeAuthorities = filters.includeAuthorities;
		query.filterNorthIsland = filters.filterNorthIsland;
		query.filterSouthIsland = filters.filterSouthIsland;
		return CompassApi.companyApi(store).companyListExcelExport(query);
	}

	function getContactList(filters: ReportFilters, store: Store<State>): Promise<FileResponse> {
		const searchQuery = new ContactListSearchQuery();
		searchQuery.keywords = filters.search || undefined;
		searchQuery.city = filters.city || undefined;
		searchQuery.island = filters.filterNorthIsland ? 'north' : filters.filterSouthIsland ? 'south' : undefined;
		searchQuery.includeInactive = !filters.onlyActive;
		searchQuery.includeCustomers = filters.includeCustomers;
		searchQuery.includeSuppliers = filters.includeSuppliers;
		searchQuery.includeOwners = filters.includeOwners;
		searchQuery.includeSiteContacts = filters.includeSiteContacts;
		searchQuery.includeContactsWithoutEmail = filters.includeContactsWithoutEmail;
		searchQuery.ignoreDuplicateEmailAddresses = !filters.allowDuplicateEmails;
		const query = new GetContactListExcelExportQuery();
		query.query = searchQuery;
		return CompassApi.contactApi(store).exportContacts(query);
	}

	function getPropertyDashboard(filters: ReportFilters, store: Store<State>): Promise<FileResponse> {
		const query = new GetPropertyDashboardQuery();
		applyPropertyQueryFilters(query, filters);
		query.includeBwofAnniversary = filters.includeBwofAnniversary;
		query.readyToIssue = filters.selectedService?.serviceId === GetBwofServiceID() ? filters.readyToIssue : false;
		query.includeSubServices = query.readyToIssue ? true : filters.includeSubServices;
		return CompassApi.propertyApi(store).dashboardExcelExport(query);
	}

	function getPropertyList(filters: ReportFilters, store: Store<State>): Promise<FileResponse> {
		const query = new GetPropertyListQuery();
		query.queryString = filters.search || undefined;
		query.onlyActive = filters.onlyActive;
		query.filterNorthIsland = filters.filterNorthIsland;
		query.filterSouthIsland = filters.filterSouthIsland;
		return CompassApi.propertyApi(store).propertyListExcelExport(query);
	}

	function getPropertyServiceAudit(filters: ReportFilters, store: Store<State>): Promise<FileResponse> {
		const query = new GetPropertyServiceAuditQuery();
		applyPropertyQueryFilters(query, filters);
		return CompassApi.propertyApi(store).propertyServiceAuditExport(query);
	}

	function getCustomerLogosExport(filters: ReportFilters, store: Store<State>): Promise<FileResponse> {
		const request = new CustomerLogosExcelExportRequest();
		request.includeDeleted = filters.includeDeletedCompanies;
		request.includeUnassignedLogos = filters.includeUnassignedLogos;
		return CompassApi.companyApi(store).customerLogosExcelExport(request);
	}

	@Options({
		name: 'ReportsPage',
	})
	export default class Reports extends Vue {

		readonly ReportField = ReportField;

		selectedReport: ReportDef | null = null;
		isExporting = false;
		services: ServiceData[] = [];
		servicesLoaded = false;

		filters: ReportFilters = blankFilters();

		readonly reports: ReportDef[] = [
			{
				key: 'companyList', label: 'Company List Export',
				fields: [ReportField.Search], searchIcon: 'pi pi-users', island: true,
				toggles: [
					{ key: 'includeCustomers', label: 'Customers' },
					{ key: 'includeSuppliers', label: 'Suppliers' },
					{ key: 'includeOwners', label: 'Owners' },
					{ key: 'includeAuthorities', label: 'Authorities' },
					{ key: 'onlyActive', label: 'Only active' },
				],
				run: getFilteredCompanySummaryList,
			},
			{
				key: 'contactList', label: 'Contact List Export',
				fields: [ReportField.Search, ReportField.City], searchLabel: 'Keywords', searchIcon: 'pi pi-user',
				toggles: [
					{ key: 'onlyActive', label: 'Only active' },
					{ key: 'includeCustomers', label: 'Customers' },
					{ key: 'includeSuppliers', label: 'Suppliers' },
					{ key: 'includeOwners', label: 'Owners' },
					{ key: 'includeSiteContacts', label: 'Site contacts' },
					{ key: 'includeContactsWithoutEmail', label: 'Missing emails' },
					{ key: 'allowDuplicateEmails', label: 'Duplicate emails' },
				],
				run: getContactList,
			},
			{
				key: 'propertyDashboard', label: 'Property Dashboard Export',
				fields: [ReportField.Search, ReportField.Service, ReportField.Dates],
				toggles: [
					{ key: 'activeProperty', label: 'Only active properties' },
					{ key: 'activeService', label: 'Only active services' },
					{ key: 'includeBwofAnniversary', label: 'Include BWOF anniversary' },
					{ key: 'readyToIssue', label: 'Ready to issue', visible: filters => filters.selectedService?.serviceId === GetBwofServiceID() },
				],
				run: getPropertyDashboard,
			},
			{
				key: 'propertyList', label: 'Property List Export',
				fields: [ReportField.Search], island: true,
				toggles: [
					{ key: 'onlyActive', label: 'Only active' },
				],
				run: getPropertyList,
			},
			{
				key: 'propertyServiceAudit', label: 'Property Service Audit',
				fields: [ReportField.Search, ReportField.Service, ReportField.Dates],
				serviceDisabled: true, defaultServiceId: GetOwnerInspectionServiceId(),
				toggles: [
					{ key: 'activeProperty', label: 'Active properties' },
					{ key: 'activeService', label: 'Active services' },
				],
				run: getPropertyServiceAudit,
			},
			{
				key: 'CustomerLogos', label: 'Customer Logo Templates Export',
				fields: [],
				toggles: [
					{ key: 'includeDeletedCompanies', label: 'Include deleted' },
					{ key: 'includeUnassignedLogos', label: 'Include unassigned logos' },
				],
				run: getCustomerLogosExport,
			},
		];

		// If the first row is search filter only, then take up available space
		get firstRowIsSearchFilterOnly(): boolean {
			const fields = this.selectedReport?.fields;
			return !!fields && fields.length === 1 && fields[0] === ReportField.Search;
		}

		get visibleToggleFilters(): ToggleDef[] {
			const toggles = this.selectedReport?.toggles ?? [];
			return toggles.filter(toggle => !toggle.visible || toggle.visible(this.filters));
		}

		get servicesList(): ServiceData[] {
			const blank = new ServiceData();
			blank.serviceName = 'All services';
			blank.serviceId = undefined;
			return [blank, ...this.services];
		}

		onReportChange() {
			this.filters = blankFilters();
			if (!this.selectedReport?.fields.includes(ReportField.Service))
				return;

			if (this.servicesLoaded)
				this.applyReportDefaults();

			else this.loadServices();
		}

		// Preselect a report's default service (e.g. Service Audit → Owner Inspection) once services are loaded.
		applyReportDefaults() {
			const defaultServiceId = this.selectedReport?.defaultServiceId;
			if (defaultServiceId === undefined)
				return;

			this.filters.selectedService = this.services.find(service => service.serviceId === defaultServiceId) ?? null;
		}

		async loadServices() {
			const apiClient = CompassApi.serviceApi(this.$store);
			const query = new GetAllAccessibleServicesQuery();
			query.sortType = 'main-first';
			query.onlyServicesInUse = true;

			try {
				this.services = await apiClient.getAllServices(query);
				this.servicesLoaded = true;
				this.applyReportDefaults();
			} catch (error) {
				Toast.ApiError('Error loading services', error as Error);
			}
		}

		async exportReport() {
			if (!this.selectedReport)
				return;

			this.isExporting = true;

			try {
				DownloadFromFileResponse(await this.selectedReport.run(this.filters, this.$store));
				Toast.Success(this.selectedReport.label, 'The export was successful.');
			} catch (error) {
				Toast.ApiError('Error isExporting report:', error as Error);
			} finally {
				this.isExporting = false;
			}
		}
	}
</script>
